import { Divider } from "@mui/material";
import { styled } from "@mui/system";
import dayjs from "dayjs";
import { compact, mapValues, uniq, uniqBy, values } from "lodash";
import * as React from "react";
import { ReactComponent as PsaIcon } from "../../assets/icons/ic_psa.svg";
import { t } from "../../i18n/util";
import {
    BloodpanelReport,
    BloodpanelReportHistory,
    BloodpanelReportValue,
    PatientInfo,
    RecommendationsReportHistory,
    StoolSampleReportHistory,
} from "../../network/APITypes";
import {
    compareBloodpanelValues,
    isDifferentialBaseName,
    OTHER_VALUES_GROUP_KEY,
    sortBloodpanelGroupNames,
} from "../util/bloodpanelDisplay";
import { Colors } from "../util/Colors";
import { formatDate, formatPatientName, getBloodpanelConspicuousnessValue, getRecommendations } from "../util/helpers";
import {
    getBloodpanelValueDisplayValue,
    getBloodpanelValueDotPosition,
    getBloodpanelValueRangeValues,
    getConspicuousnessColor,
    getConspicuousnessDisplayLabel,
    getConspicuousnessDropdownLabel,
    getHistoryHeaderItems,
    getSmileyByColor,
    HistoryTableHeaderItem,
} from "../util/ValueDisplayHelpers";
import { Card } from "./Card";
import { ConspicuousnessDisplay } from "./ConspicuousnessDisplay";
import { CurrentValueTable } from "./CurrentValueTable";
import { HistoryValuesTable } from "./HistoryValuesTable";
import { RecommendationHistory } from "./RecommendationHistory";
import { HistorySectionTitle } from "./HistorySectionTitle";
import { ValueDisplay } from "./ValueDisplay";
import { VerticalTableDivider } from "./VerticalTableDivider";

const PSA_NAME = "psa";
const STOOLSAMPLE = "stoolsample";
const VACCINATIONS = "general.vaccinations";
const BLOODPANEL = "bloodpanel";

type IProps = {
    bloodpanels?: BloodpanelReportHistory;
    stoolSample?: StoolSampleReportHistory;
    recommendations?: RecommendationsReportHistory;
    patientInformation?: PatientInfo;
    showBloodpanels?: boolean;
    showStoolSample?: boolean;
};

type IValueItem = {
    name?: string;
    unit?: string;
    valueType?: string;
};

const Title = styled("div")({
    margin: "40px 0",
    fontWeight: 600,
    fontSize: 19,
});

const TitleRowContainer = styled("div")({
    display: "flex",
    height: 45,
    alignItems: "center",
    backgroundColor: Colors.LIGHT_BACKGROUND,
    marginLeft: 16,
});

const Comment = styled("div")({
    height: 52,
    display: "flex",
    alignItems: "center",
    marginLeft: 16,
    fontWeight: 400,
    fontSize: 16,
    color: Colors.TEXT_LIGHT,
});

const Subtitle = styled("div")({
    color: Colors.DISABLED,
    fontWeight: 400,
});

const TitleRow = ({ title, subtitle, icon }: { title?: string; subtitle?: string; icon?: React.ReactNode }) => (
    <TitleRowContainer style={{ height: icon ? 65 : undefined }}>
        {icon && (
            <div
                style={{
                    padding: 9,
                    backgroundColor: Colors.WHITE,
                    borderRadius: 8,
                    border: `1px solid ${Colors.GREY_200}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 12,
                }}
            >
                {icon}
            </div>
        )}
        <HistorySectionTitle style={{ margin: 0, display: subtitle ? "block" : undefined }}>
            {title}
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HistorySectionTitle>
    </TitleRowContainer>
);

/**
 * Identity used to merge/match the same test across checkups. Values are merged by name alone
 * (unit notation can differ across checkups/labs for the same test, e.g. TSH: "mIU/l" vs
 * "µIU/ml") — with two exceptions that must stay on separate rows despite sharing a name:
 *  - the differential blood count parameters, which report a % and a G/l (absolute) value
 *    under the identical name, and
 *  - a boolean screening result (e.g. a urine dipstick "negative/positive") that shares its
 *    name with a genuine numeric lab value (e.g. Erythrozyten, Leukozyten, Bilirubin) — these
 *    are different measurements, not the same test reported differently. Only "bool" is
 *    special-cased here (not "text"), since some checkups report an otherwise-numeric value as
 *    a text placeholder (e.g. "unauffaellig") for the very same test — that must still merge.
 */
const getValueMergeKey = (name?: string, unit?: string, valueType?: string) => {
    const normalizedName = name?.trim().toLowerCase() ?? "";
    if (isDifferentialBaseName(name)) {
        return `${normalizedName}_${unit?.trim().toLowerCase() ?? ""}`;
    }
    return `${normalizedName}_${valueType === "bool" ? "bool" : ""}`;
};

const getBloodpanelValueItemsForGroup = (
    group: string,
    checkupReport?: BloodpanelReport,
    allReports?: BloodpanelReport[],
) => {
    // Special handling for "Weitere Werte" group - collect values from groups without names
    if (group === OTHER_VALUES_GROUP_KEY) {
        // Get all named groups from ALL reports to check for duplicates
        const allNamedGroups =
            allReports?.flatMap((report) => report?.values?.filter((groupItem) => groupItem.name) ?? []) ?? [];
        const namedGroupValues = new Set<string>();

        // Collect all value names from all named groups across all reports
        allNamedGroups.forEach((groupItem) => {
            groupItem.values?.forEach((valueItem) => {
                if (getBloodpanelValueDisplayValue(valueItem)) {
                    namedGroupValues.add(getValueMergeKey(valueItem.name, valueItem.unit, valueItem.valueType));
                }
            });
        });

        // Only return values from unnamed groups that don't exist in any named groups
        return (
            checkupReport?.values
                ?.filter(
                    (groupItem) =>
                        !groupItem.name && groupItem.values?.some((value) => getBloodpanelValueDisplayValue(value)),
                )
                ?.flatMap(
                    (groupItem) =>
                        groupItem.values?.flatMap((valueItem) =>
                            getBloodpanelValueDisplayValue(valueItem) &&
                            !namedGroupValues.has(getValueMergeKey(valueItem.name, valueItem.unit, valueItem.valueType))
                                ? {
                                      name: valueItem.name,
                                      unit: valueItem.unit,
                                      valueType: valueItem.valueType,
                                  }
                                : [],
                        ) ?? [],
                ) ?? []
        );
    }

    // Regular handling for named groups
    return (
        checkupReport?.values
            ?.find((groupItem) => groupItem.name === group)
            ?.values?.flatMap((valueItem) =>
                getBloodpanelValueDisplayValue(valueItem)
                    ? {
                          name: valueItem.name,
                          unit: valueItem.unit,
                          valueType: valueItem.valueType,
                      }
                    : [],
            ) ?? []
    );
};

/**
 * Returns the names of the groups that have at least one value that is not undefined.
 * @param checkupReport - The checkup report to get the group names from.
 * @returns The names of the groups that have at least one value that is not undefined.
 */
const getBloodpanelGroupNames = (checkupReport?: BloodpanelReport) =>
    checkupReport?.values?.flatMap((group) => {
        const groupHasValidValue = group.values?.some((value) => getBloodpanelValueDisplayValue(value));

        // If the group has valid values but no name, we'll handle it specially
        if (groupHasValidValue && !group.name) {
            return [OTHER_VALUES_GROUP_KEY];
        }

        return groupHasValidValue ? group.name ?? [] : [];
    }) ?? [];

const BloodpanelHistory = ({
    bloodpanels,
    lastCheckupReport,
    latestHistoryItems,
    emptyColumns,
    currentValueHeaderItems,
    valueHeaderItems,
}: {
    bloodpanels?: BloodpanelReportHistory;
    lastCheckupReport?: BloodpanelReport & { checkupDate?: string | Date | undefined };
    latestHistoryItems: (BloodpanelReport & { checkupDate?: string | Date | undefined })[];
    emptyColumns: { content: string }[];
    currentValueHeaderItems: {
        name: string;
        topLabel?: string;
        date?: string;
        patientName?: string;
    }[];
    valueHeaderItems: HistoryTableHeaderItem[];
}) => {
    // Get the names of the groups that have at least one value that is not undefined.
    // This is used to filter out groups that have no values.
    // Groups without names are combined into "Weitere Werte".
    // Merge group names from latest + history; order per mapping (Vorgeschlagene Gruppierung).
    const bloodpanelGroups = sortBloodpanelGroupNames(
        uniq([
            ...getBloodpanelGroupNames(bloodpanels?.latest),
            ...([...latestHistoryItems, lastCheckupReport]?.flatMap((history) => getBloodpanelGroupNames(history)) ??
                []),
        ]),
    );

    // Get the value items for each group.
    // This is used to display the value items in the current value and history tables.
    const allReports = [bloodpanels?.latest, ...latestHistoryItems, lastCheckupReport].filter(
        Boolean,
    ) as BloodpanelReport[];

    const valueItemsByGroup = bloodpanelGroups.reduce((accumulator, group) => {
        const latestValueItems = getBloodpanelValueItemsForGroup(group, bloodpanels?.latest, allReports);

        const historyValueItems =
            [...latestHistoryItems, lastCheckupReport]?.flatMap((historyItem) =>
                getBloodpanelValueItemsForGroup(group, historyItem, allReports),
            ) ?? [];

        const allValueItems = uniqBy(
            [...latestValueItems, ...historyValueItems],
            (valueItem) => getValueMergeKey(valueItem.name, valueItem.unit, valueItem.valueType),
        ).sort(compareBloodpanelValues);

        // Only include groups that have actual values
        if (allValueItems.length > 0) {
            return {
                ...accumulator,
                [group]: allValueItems,
            };
        }

        return accumulator;
    }, {} as { [key: string]: IValueItem[] });

    // Only render groups that actually have values.
    // `bloodpanelGroups` can contain groups that end up empty after filtering/deduplication.
    const displayGroups = sortBloodpanelGroupNames(Object.keys(valueItemsByGroup));

    // Merge the latest and history value items for each group.
    // This creates one row per unique test value, with both current and historical data.
    // Each valueItem represents a unique test (name + unit combination).
    const mergedHistories = mapValues(valueItemsByGroup, (valueItems, group) => {
        return valueItems.map((valueItem) => {
            const latestBloodpanelValue: BloodpanelReportValue | undefined = (() => {
                if (group === OTHER_VALUES_GROUP_KEY) {
                    // For "Weitere Werte", search in all groups without names
                    return bloodpanels?.latest?.values
                        ?.filter((groupItem) => !groupItem.name)
                        ?.flatMap((groupItem) => groupItem.values ?? [])
                        ?.find(
                            (bloodpanelValueItem) =>
                                bloodpanelValueItem.name === valueItem.name &&
                                bloodpanelValueItem.unit === valueItem.unit,
                        );
                } else {
                    // For named groups, search in both the named group and unnamed groups
                    // This allows values from unnamed groups to be merged into named groups
                    const namedGroupValue = bloodpanels?.latest?.values
                        ?.find((groupItem) => groupItem.name === group)
                        ?.values?.find(
                            (bloodpanelValueItem) =>
                                bloodpanelValueItem.name === valueItem.name &&
                                bloodpanelValueItem.unit === valueItem.unit,
                        );

                    if (namedGroupValue) {
                        return namedGroupValue;
                    }

                    // If not found in named group, check unnamed groups
                    return bloodpanels?.latest?.values
                        ?.filter((groupItem) => !groupItem.name)
                        ?.flatMap((groupItem) => groupItem.values ?? [])
                        ?.find(
                            (bloodpanelValueItem) =>
                                bloodpanelValueItem.name === valueItem.name &&
                                bloodpanelValueItem.unit === valueItem.unit,
                        );
                }
            })();

            const range = getBloodpanelValueRangeValues(latestBloodpanelValue);
            const displayValue = getBloodpanelValueDisplayValue(latestBloodpanelValue);

            // Indicates if there is a reference value to compare the value to.
            // If there is no reference value we can't show if the value is good or bad.
            // For bool and text values we don't have a reference value.
            // See: https://allaboutapps-at.atlassian.net/browse/VOR-335
            const hasReferenceValue =
                latestBloodpanelValue?.valueType !== "bool" &&
                latestBloodpanelValue?.valueType !== "text" &&
                latestBloodpanelValue?.rangeType;

            const dotPosition = getBloodpanelValueDotPosition(latestBloodpanelValue, range, displayValue);
            const isDangerValue = dotPosition === "high" || dotPosition === "low";
            const color = hasReferenceValue && isDangerValue ? Colors.VALUE_DISPLAY_ORANGE : undefined;
            const smileyColor = isDangerValue ? Colors.VALUE_DISPLAY_ORANGE : Colors.VALUE_DISPLAY_GREEN;
            const smiley = displayValue !== undefined ? getSmileyByColor(smileyColor) : null;
            const hasValue = displayValue !== undefined;

            const latest =
                displayValue !== undefined
                    ? [
                          {
                              content: valueItem.name,
                          },
                          {
                              content: hasValue ? displayValue : "-",
                              color,
                              adornmentText: hasValue ? valueItem.unit : undefined,
                          },
                          {
                              content: hasReferenceValue ? (
                                  range?.minVal !== undefined && range.maxVal !== undefined ? (
                                      <ValueDisplay dotPosition={dotPosition} values={range} />
                                  ) : (
                                      <>
                                          {getBloodpanelConspicuousnessValue(latestBloodpanelValue) ? (
                                              <ConspicuousnessDisplay
                                                  reverse={
                                                      latestBloodpanelValue?.shortName === "HDL" ||
                                                      latestBloodpanelValue?.name === "HDL-Cholesterin"
                                                  }
                                                  label={getConspicuousnessDisplayLabel(latestBloodpanelValue)}
                                                  value={getBloodpanelConspicuousnessValue(latestBloodpanelValue)}
                                              />
                                          ) : (
                                              "-"
                                          )}
                                      </>
                                  )
                              ) : (
                                  "-"
                              ),
                          },
                      ]
                    : [
                          {
                              content: valueItem.name,
                          },
                          { content: "-" },
                          {
                              content: "-",
                          },
                      ];

            // Look up this specific test value in each historical report
            // This ensures the same test appears in the same row across all time periods
            const historyBloodpanelValues = latestHistoryItems.map((historyItem) => {
                const currentValue = valueItem.name ? getBloodpanelValue(valueItem, historyItem) : undefined;
                return currentValue !== undefined ? getBloodpanelHistoryItem(currentValue) : { content: "-" };
            });

            const lastBloodpanelValue = valueItem.name ? getBloodpanelValue(valueItem, lastCheckupReport) : undefined;
            const lastBloodpanelItem =
                lastBloodpanelValue !== undefined ? getBloodpanelHistoryItem(lastBloodpanelValue) : { content: "-" };

            const history = [
                {
                    content: hasReferenceValue ? smiley : "-",
                },
                ...emptyColumns,
                ...historyBloodpanelValues,
                lastBloodpanelItem,
            ];

            return {
                latest,
                history,
            };
        });
    });

    return (
        <>
            {displayGroups.map((group) => (
                <React.Fragment key={group}>
                    <TitleRow title={group === OTHER_VALUES_GROUP_KEY ? t("screen.history.otherValues") : group} />
                    <Divider />
                    <div style={{ display: "flex", width: "100%" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <CurrentValueTable
                                headerItems={currentValueHeaderItems}
                                rows={mergedHistories[group].map((groupHistory) => groupHistory.latest)}
                                stickyHeader
                            />
                        </div>
                        <VerticalTableDivider />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <HistoryValuesTable
                                headerItems={valueHeaderItems}
                                rows={mergedHistories[group].map((groupHistory) => groupHistory.history)}
                                stickyHeader
                            />
                        </div>
                    </div>
                </React.Fragment>
            ))}
        </>
    );
};

const findBloodpanelValue = (value: BloodpanelReportValue, item: IValueItem) =>
    getValueMergeKey(value.name, value.unit, value.valueType) === getValueMergeKey(item.name, item.unit, item.valueType);

const getBloodpanelValue = (item: IValueItem, checkupReport?: BloodpanelReport) => {
    // Search in all groups (both named and unnamed)
    const group = checkupReport?.values?.find((group) =>
        group.values?.find((value) => findBloodpanelValue(value, item)),
    );
    return group?.values?.find((value) => findBloodpanelValue(value, item));
};

const getBloodpanelHistoryItem = (bloodpanelValue?: BloodpanelReportValue) => {
    if (bloodpanelValue === undefined) {
        return {
            content: "-",
        };
    }

    const bloodpanelValueRange = getBloodpanelValueRangeValues(bloodpanelValue);
    const bloodpanelValueDisplayValue = getBloodpanelValueDisplayValue(bloodpanelValue);

    const dotPosition = getBloodpanelValueDotPosition(
        bloodpanelValue,
        bloodpanelValueRange,
        bloodpanelValueDisplayValue,
    );

    const isDangerValue = dotPosition === "high" || dotPosition === "low";

    // Indicates if there is a reference value to compare the value to.
    // If there is no reference value we can't show if the value is good or bad.
    // For bool and text values we don't have a reference value.
    // See: https://allaboutapps-at.atlassian.net/browse/VOR-335
    const hasReferenceValue =
        bloodpanelValue.valueType !== "bool" && bloodpanelValue.valueType !== "text" && bloodpanelValue?.rangeType;

    const hasValue = bloodpanelValueDisplayValue !== undefined;

    return {
        content: hasValue ? bloodpanelValueDisplayValue : "-",
        color: hasReferenceValue && isDangerValue ? Colors.VALUE_DISPLAY_ORANGE : undefined,
        adornmentText: hasValue ? bloodpanelValue.unit : undefined,
    };
};

export const LaboratoryResultsHistory = ({
    bloodpanels,
    stoolSample,
    recommendations,
    patientInformation,
    showBloodpanels,
    showStoolSample,
}: IProps) => {
    const currentValueHeaderItems = [
        {
            name: "name",
            topLabel: t("screen.history.table.checkup"),
        },
        {
            name: "value",
            topLabel: t("screen.history.table.currentValueLabel"),
            date: formatDate(bloodpanels?.latest?.date),
            patientName: formatPatientName(patientInformation),
        },
        {
            name: "referenceRange",
            topLabel: t("screen.history.table.referenceRange"),
        },
    ];

    const psa = getBloodpanelValue({ name: PSA_NAME }, bloodpanels?.latest);

    const psaRangeValues = getBloodpanelValueRangeValues(psa);
    const psaDisplayValue = getBloodpanelValueDisplayValue(psa);

    // Indicates if there is a reference value to compare the value to.
    // If there is no reference value we can't show if the value is good or bad.
    // For bool and text values we don't have a reference value.
    // See: https://allaboutapps-at.atlassian.net/browse/VOR-335
    const hasReferenceValue = psa?.valueType !== "bool" && psa?.valueType !== "text" && psa?.rangeType;

    const psaDotPosition = getBloodpanelValueDotPosition(psa, psaRangeValues, psaDisplayValue);

    const isPsaConspicuous = psaDotPosition === "high" || psaDotPosition === "low";
    const psaColor = hasReferenceValue && isPsaConspicuous ? Colors.VALUE_DISPLAY_ORANGE : undefined;

    const psaCurrentValueRow = [
        { content: t("screen.history.table.psa") },
        {
            content: psaDisplayValue ?? "-",
            color: psaColor,
            adornmentText: psaDisplayValue !== undefined ? psa?.unit : undefined,
        },
        {
            content: <ValueDisplay dotPosition={psaDotPosition} values={psaRangeValues} />,
        },
    ];

    const {
        valueHeaderItems: bloodpanelValueHeaderItems,
        latestHistoryItems: latestBloodpanelHistoryItems,
        lastCheckupReport: lastBloodpanelCheckupReport,
        emptyColumns: emptyBloodpanelColumns,
    } = getHistoryHeaderItems(
        bloodpanels?.history?.map((historyItem) => ({ ...historyItem, checkupDate: historyItem.date })),
        patientInformation,
    );

    const latestPsaHistory = latestBloodpanelHistoryItems.map((checkupReport) => {
        const currentPsa = getBloodpanelValue({ name: PSA_NAME }, checkupReport);
        return getBloodpanelHistoryItem(currentPsa);
    });
    const lastPsa = lastBloodpanelCheckupReport?.values
        ?.find((group) => group.values?.find((value) => findBloodpanelValue(value, { name: PSA_NAME })))
        ?.values?.find((value) => findBloodpanelValue(value, { name: PSA_NAME }));
    const lastPsaItem = getBloodpanelHistoryItem(lastPsa);
    const psaSmiley = getSmileyByColor(psaColor);
    const psaHistoryValuesRow = [{ content: psaSmiley }, ...emptyBloodpanelColumns, ...latestPsaHistory, lastPsaItem];

    const {
        valueHeaderItems: stoolSampleValueHeaderItems,
        latestHistoryItems: latestStoolSampleHistoryItems,
        lastCheckupReport: lastStoolSampleCheckupReport,
        emptyColumns: emptyStoolSampleColumns,
    } = getHistoryHeaderItems(stoolSample?.history, patientInformation);
    const stoolSampleColor = getConspicuousnessColor(stoolSample?.latest?.value);
    const stoolSampleCurrentValueRow = [
        {
            content: t("screen.history.stool"),
        },
        {
            content: stoolSample?.latest?.value ? getConspicuousnessDropdownLabel(stoolSample?.latest?.value) : "-",
            color: stoolSampleColor,
        },
        {
            content: stoolSample?.latest?.value ? <ConspicuousnessDisplay value={stoolSample?.latest?.value} /> : "-",
        },
    ];

    const latestStoolSampleHistory = latestStoolSampleHistoryItems.map((stoolSampleItem) => ({
        content: getConspicuousnessDropdownLabel(stoolSampleItem.value),
        color: getConspicuousnessColor(stoolSampleItem.value),
    }));
    const lastStoolSampleItem = {
        content: getConspicuousnessDropdownLabel(lastStoolSampleCheckupReport?.value),
        color: getConspicuousnessColor(lastStoolSampleCheckupReport?.value),
    };

    const stoolSampleHistoryValuesRow = [
        { content: getSmileyByColor(stoolSampleColor) },
        ...emptyStoolSampleColumns,
        ...latestStoolSampleHistory,
        lastStoolSampleItem,
    ];

    const {
        currentRecommendation: stoolSampleRecommendation,
        currentRecommendationHistory: stoolSampleRecommendationHistory,
    } = getRecommendations(STOOLSAMPLE, recommendations);

    const {
        currentRecommendation: vaccinationRecommendation,
        currentRecommendationHistory: vaccinationRecommendationHistory,
    } = getRecommendations(VACCINATIONS, recommendations);

    const {
        currentRecommendation: bloodpanelRecommendation,
        currentRecommendationHistory: bloodpanelRecommendationHistory,
    } = getRecommendations(BLOODPANEL, recommendations);

    const patientAge = dayjs(dayjs()).diff(dayjs(patientInformation?.birthday), "years");

    const patientName = formatPatientName(patientInformation);

    return (
        <>
            <Title>
                {t("screen.history.laboratoryResults")}
                {patientName && (
                    <div style={{ fontSize: 14, fontWeight: 400, color: Colors.DISABLED, marginTop: 4 }}>
                        {patientName}
                    </div>
                )}
            </Title>
            {showBloodpanels && (
                <>
                    <Card>
                        <HistorySectionTitle>{t("screen.history.bloodpanels")}</HistorySectionTitle>
                        <Divider />
                        {psa && patientAge >= 60 && patientAge <= 69 && (
                            <>
                                <TitleRow
                                    title={psa.name}
                                    subtitle={t("screen.history.laboratoryResults.psa.subtitle")}
                                    icon={<PsaIcon />}
                                />
                                <div style={{ display: "flex", width: "100%" }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <CurrentValueTable
                                            headerItems={currentValueHeaderItems}
                                            rows={[psaCurrentValueRow]}
                                            style={{ borderTop: `1px solid ${Colors.GREY_200}` }}
                                            stickyHeader
                                        />
                                    </div>
                                    <VerticalTableDivider />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <HistoryValuesTable
                                            headerItems={bloodpanelValueHeaderItems}
                                            rows={[psaHistoryValuesRow]}
                                            style={{ borderTop: `1px solid ${Colors.GREY_200}` }}
                                            stickyHeader
                                        />
                                    </div>
                                </div>
                                <Comment>{psa.commentTxt}</Comment>
                                <Divider />
                            </>
                        )}
                        <BloodpanelHistory
                            bloodpanels={bloodpanels}
                            currentValueHeaderItems={currentValueHeaderItems}
                            latestHistoryItems={latestBloodpanelHistoryItems}
                            emptyColumns={emptyBloodpanelColumns}
                            lastCheckupReport={lastBloodpanelCheckupReport}
                            valueHeaderItems={bloodpanelValueHeaderItems}
                        />
                        <RecommendationHistory
                            title={"screen.history.recommendation"}
                            recommendation={bloodpanelRecommendation}
                            recommendationHistory={bloodpanelRecommendationHistory}
                        />
                    </Card>
                    <Card style={{ marginTop: 24 }}>
                        <RecommendationHistory
                            title="screen.history.vaccinationRecommendation"
                            recommendation={vaccinationRecommendation}
                            recommendationHistory={vaccinationRecommendationHistory}
                        />
                    </Card>
                </>
            )}
            {showStoolSample && stoolSample && (
                <Card style={{ marginTop: 24 }}>
                    <HistorySectionTitle>{t("screen.history.stoolSample")}</HistorySectionTitle>
                    <Divider />
                    <div style={{ display: "flex", width: "100%" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <CurrentValueTable
                                headerItems={currentValueHeaderItems}
                                rows={[stoolSampleCurrentValueRow]}
                                stickyHeader
                            />
                        </div>
                        <VerticalTableDivider />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <HistoryValuesTable
                                headerItems={stoolSampleValueHeaderItems}
                                rows={[stoolSampleHistoryValuesRow]}
                                stickyHeader
                            />
                        </div>
                    </div>
                    <RecommendationHistory
                        title={"screen.history.recommendation"}
                        recommendation={stoolSampleRecommendation}
                        recommendationHistory={stoolSampleRecommendationHistory}
                    />
                </Card>
            )}
        </>
    );
};
